import { Form, Formik } from 'formik';
import React, { ReactNode, useState } from 'react';
import { DropzoneProps } from 'react-dropzone';
import * as yup from 'yup';

import { Loader } from '@sbu/components.loader';
import { useToasts } from '@sbu/contexts.toasts';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
} from '@sumologic/yama-ds-components';

import css from './FileUpload.module.scss';
import { FileUploadField, FileUploadFieldProps } from './FileUploadField';

interface FileUploadProps {
  accept?: DropzoneProps['accept'];
  cancelLabel?: ReactNode;
  dialogProps?: DialogProps;
  fileUploadFieldProps?: FileUploadFieldProps;
  header?: ReactNode;
  intro?: ReactNode;
  label?: ReactNode;
  maxFileSize?: DropzoneProps['maxSize'];
  onSubmit: Function;
  uploading?: boolean;
  uploadLabel?: ReactNode;
  isOpen?: boolean;
  showButton?: boolean;
  onClose?: () => void;
}

export interface FormValues {
  file?: File;
}

const SUPPORTED_FORMATS = [
  '.csv',
  'application/csv',
  'application/vnd.ms-excel',
  'application/x-csv',
  'text/comma-separated-values',
  'text/csv',
  'text/x-comma-separated-values',
  'text/x-csv',
];

const DEFAULT_MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export function FileUpload({
  accept = SUPPORTED_FORMATS,
  cancelLabel = 'Cancel',
  dialogProps,
  fileUploadFieldProps,
  header,
  intro = null,
  label = 'Import Metadata',
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  onSubmit,
  uploading = false,
  uploadLabel = 'Upload',
  isOpen = false,
  showButton = true,
  onClose,
}: FileUploadProps) {
  const [open, setOpen] = useState(isOpen);
  const { add: addInfo } = useToasts();

  const handleClose = () => {
    setOpen(false);
    onClose?.();
  };

  const handleSubmit = async (values: FormValues, { setFieldValue }) => {
    await onSubmit({ addInfo, values });
    setFieldValue('file', undefined);
    handleClose();
  };

  const validationSchema = yup.object().shape({
    file: yup
      .mixed()
      .test('fileSize', 'File Size is too large', (f) =>
        maxFileSize ? f?.size <= maxFileSize : true
      ),
  });
  const initialValues: FormValues = {
    file: undefined,
  };
  return (
    <>
      {showButton && (
        <Button
          className={css.importButton}
          onClick={() => setOpen(true)}
          color='secondary'
        >
          {label}
        </Button>
      )}
      <Dialog
        className={css.dialog}
        data-sumo-test='fileUpload'
        onClose={handleClose}
        open={open}
        maxWidth='md'
        fullWidth={true}
        aria-labelledby='file-upload-dialog-title'
        {...dialogProps}
      >
        <DialogTitle id='file-upload-dialog-title'>{header}</DialogTitle>
        <DialogContent>
          {uploading ? (
            <Loader minHeight={200} minWidth={400} size='large' />
          ) : (
            <Formik
              initialValues={initialValues}
              onSubmit={handleSubmit}
              validationSchema={validationSchema}
            >
              {({ setFieldValue, values }) => (
                <Form>
                  <div className={css.dialogBody}>
                    {intro}
                    <FileUploadField
                      dropzoneProps={{ accept, maxSize: maxFileSize }}
                      setFieldValue={setFieldValue}
                      values={values}
                      {...fileUploadFieldProps}
                    />
                  </div>
                  <DialogActions>
                    <Button
                      color='secondary'
                      onClick={handleClose}
                      type='button'
                    >
                      {cancelLabel}
                    </Button>
                    <Button disabled={!values.file} type='submit'>
                      {uploadLabel}
                    </Button>
                  </DialogActions>
                </Form>
              )}
            </Formik>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
